import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CelebrationStreakOutline,
	Checkbox,
	ClappingHandsOutline,
	CurrencyDollarOutline,
	Divider,
	FilterLinesOutline,
	HashtagOutline,
	HStack,
	IconButton,
	Input,
	InputGroup,
	InputLeftElement,
	Popover,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	Portal,
	rem,
	SearchOutline,
	Spacer,
	Text,
	Tooltip,
	TrophyOutline,
	useDisclosure,
	VStack,
} from '@bonusly/design-system';
import { Permission, usePermission } from '@bonusly/who-am-i';

import { Reveal } from '../../../../../../components/display/reveal';
import { useSeasonalTheme } from '../../../../../../components/seasonal/seasonal-theme-context';
import { FilterType, Option, useRecognitionFeedFilterOptions } from '../../api/hooks/useRecognitionFeedFilterOptions';
import { AdditionalFilters } from '../../hooks/useAdditionalFilters';

const getTypeIcon = (value: string): ReactNode | null => {
	switch (value.toLowerCase()) {
		case 'peer':
			return <ClappingHandsOutline size="md" color="text.default" />;
		case 'awards':
			return <TrophyOutline size="md" color="text.default" />;
		case 'incentives':
			return <CurrencyDollarOutline size="md" color="text.default" />;
		case 'celebrations':
			return <CelebrationStreakOutline size="md" color="text.default" />;
		default:
			return null;
	}
};

interface AdditionalFiltersDropdownProps {
	hasAppliedFilters: boolean;
	hasPendingChanges: boolean;
	onApply: () => void;
	onCancel: () => void;
	onClearAll: () => void;
	onToggleHashtag: (hashtag: string) => void;
	onToggleType: (type: string) => void;
	pendingFilters: AdditionalFilters;
}

export const AdditionalFiltersDropdown = ({
	hasAppliedFilters,
	hasPendingChanges,
	onApply,
	onCancel,
	onClearAll,
	onToggleHashtag,
	onToggleType,
	pendingFilters,
}: AdditionalFiltersDropdownProps) => {
	const { t } = useTranslation('recognition');
	const canAccessAwardsExperience = usePermission(Permission.CanAccessAwardsExperience);
	const canAccessIncentivesExperience = usePermission(Permission.CanAccessIncentivesExperience);
	const canAccessCelebrationsExperience = usePermission(Permission.CanAccessCelebrationsExperience);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [searchTerm, setSearchTerm] = useState('');
	const { isSeasonalThemeEnabled } = useSeasonalTheme();

	const { data: filterOptions } = useRecognitionFeedFilterOptions();

	const typeOptions = useMemo(() => {
		return filterOptions?.find((option) => option.filter_type === FilterType.RecognitionType)?.options || [];
	}, [filterOptions]);

	const hashtagOptions = useMemo(() => {
		const baseOptions = filterOptions?.find((option) => option.filter_type === FilterType.Hashtag)?.options || [];

		// Add custom hashtags from pending filters that aren't in the base options
		const customHashtags = pendingFilters.hashtags
			.filter((hashtag) => !baseOptions.some((option) => option.value === hashtag))
			.map((hashtag) => ({ name: hashtag, value: hashtag }));

		return [...baseOptions, ...customHashtags];
	}, [filterOptions, pendingFilters.hashtags]);

	const filteredTypeOptions = useMemo(() => {
		// NOTE: Order by value: peer, awards, incentives, celebrations
		const typeOrder = ['peer', 'awards', 'incentives', 'celebrations'];
		const orderedOptions = [...typeOptions].sort((a, b) => typeOrder.indexOf(a.value as string) - typeOrder.indexOf(b.value as string));
		const permissionFiltered = orderedOptions.filter((option) => {
			const value = String(option.value);
			if (value === 'awards') return canAccessAwardsExperience;
			if (value === 'incentives') return canAccessIncentivesExperience;
			if (value === 'celebrations') return canAccessCelebrationsExperience;
			return true;
		});
		if (!searchTerm) return permissionFiltered;
		const lowerSearchTerm = searchTerm.toLowerCase();
		return permissionFiltered.filter((option) => {
			const translatedLabel = t(`bonus_feed.additional_filters.types.${option.value}`, option.name);
			return translatedLabel.toLowerCase().includes(lowerSearchTerm);
		});
	}, [typeOptions, searchTerm, t, canAccessAwardsExperience, canAccessCelebrationsExperience, canAccessIncentivesExperience]);

	const filteredHashtagOptions = useMemo(() => {
		if (!searchTerm) return hashtagOptions;
		return hashtagOptions.filter((option) => option.name.toLowerCase().includes(searchTerm.toLowerCase()));
	}, [hashtagOptions, searchTerm]);

	const handleOpen = () => {
		setSearchTerm('');
		onCancel(); // Sync pending filters to current applied filters from URL
		onOpen();
	};

	const handleCancel = () => {
		onCancel();
		onClose();
	};

	const handleApply = () => {
		onApply();
		onClose();
	};

	const handleReset = () => {
		onClearAll();
		onClose();
	};

	const selectedCount = pendingFilters.types.length + pendingFilters.hashtags.length;

	return (
		<Popover isOpen={isOpen} onClose={handleCancel} placement="bottom-end" offset={[0, 2]}>
			<Tooltip label={t('bonus_feed.additional_filters.button_label')} placement="top">
				<Box position="relative" display="inline-block">
					<PopoverTrigger>
						<IconButton
							aria-label={t('bonus_feed.additional_filters.button_label')}
							icon={<FilterLinesOutline size="sm" />}
							variant="outline"
							{...(isSeasonalThemeEnabled ? { bg: 'bg.minipage', backdropFilter: 'blur(44px)' } : {})}
							borderColor={selectedCount > 0 ? 'border.focus' : 'border.default'}
							size="md"
							onClick={handleOpen}
							colorScheme={hasAppliedFilters ? 'primary' : 'secondary'}
							p={rem(8)}
							outline={isOpen ? '2px solid' : undefined}
							outlineColor={isOpen ? 'border.focus' : undefined}
							outlineOffset={isOpen ? '-2px' : undefined}
							{...(isSeasonalThemeEnabled ? { _hover: { bg: 'bg.minipage' } } : {})}
						/>
					</PopoverTrigger>
					{hasAppliedFilters && (
						<Box
							position="absolute"
							top={-1}
							right={-1}
							w={rem(18)}
							h={rem(18)}
							borderRadius="full"
							bg="primary.bg"
							color="text.onColor"
							fontSize="xs"
							display="flex"
							alignItems="center"
							justifyContent="center"
							pointerEvents="none"
						>
							{selectedCount > 9 ? '9+' : selectedCount}
						</Box>
					)}
				</Box>
			</Tooltip>
			<Portal>
				<PopoverContent minW={rem(290)} w="fit-content">
					<PopoverBody p={0}>
						<VStack align="stretch" spacing={rem(4)}>
							{/* Header */}
							<HStack px={rem(20)} pb={rem(12)} pt={rem(16)} justifyContent="space-between" alignItems="center">
								<HStack spacing={rem(8)}>
									<Text fontSize="md" fontWeight="semibold">
										{t('bonus_feed.additional_filters.title')}
									</Text>
									{selectedCount > 0 && (
										<Box bg="primary.bgAlpha" color="primary.fgStrong" px={rem(8)} py={rem(2)} borderRadius="full" fontSize="xs" fontWeight="semibold">
											{selectedCount}
										</Box>
									)}
								</HStack>
								<Button
									variant="link"
									colorScheme="primary"
									size="sm"
									onClick={handleReset}
									px={rem(8)}
									_focusVisible={{ outline: '2px solid', outlineColor: 'border.focus', outlineOffset: '-2px' }}
								>
									{t('bonus_feed.additional_filters.reset')}
								</Button>
							</HStack>

							<Divider />

							{/* Search Input */}
							<Box px={rem(12)} pt={rem(8)} pb={rem(4)}>
								<InputGroup size="sm">
									<InputLeftElement>
										<SearchOutline size="xs" />
									</InputLeftElement>
									<Input
										value={searchTerm}
										onChange={(e) => setSearchTerm(e.target.value)}
										placeholder={t('bonus_feed.additional_filters.search_placeholder')}
										borderRadius="full"
									/>
								</InputGroup>
							</Box>

							{/* Scrollable Content */}
							<Box maxH={rem(260)} overflowY="auto">
								{/* Types Section */}
								{filteredTypeOptions.length > 0 && (
									<VStack align="stretch" spacing={rem(4)} p={rem(8)} mb={rem(8)}>
										<Text fontSize="sm" color="text.softest" px={rem(8)}>
											{t('bonus_feed.additional_filters.types_section')}
										</Text>
										{filteredTypeOptions.map((option) => (
											<FilterCheckbox
												key={String(option.value)}
												option={option}
												isChecked={pendingFilters.types.includes(String(option.value))}
												onChange={() => onToggleType(String(option.value))}
												icon={getTypeIcon(String(option.value))}
												section="types"
											/>
										))}
									</VStack>
								)}

								{/* Hashtags Section */}
								{filteredHashtagOptions.length > 0 && (
									<VStack align="stretch" spacing={rem(4)} p={rem(8)} mb={rem(8)}>
										<Text fontSize="sm" color="text.softest" px={rem(8)}>
											{t('bonus_feed.additional_filters.hashtags_section')}
										</Text>
										{filteredHashtagOptions.map((option) => (
											<FilterCheckbox
												key={String(option.value)}
												option={{ name: option.name.replace(/^#/, ''), value: option.value }}
												icon={<HashtagOutline size="md" color="text.default" />}
												isChecked={pendingFilters.hashtags.includes(String(option.value))}
												onChange={() => onToggleHashtag(String(option.value))}
												section="hashtags"
											/>
										))}
									</VStack>
								)}

								{/* No Results */}
								{filteredTypeOptions.length === 0 && filteredHashtagOptions.length === 0 && (
									<Text fontSize="sm" textAlign="center" py={rem(16)}>
										{t('bonus_feed.additional_filters.no_results')}
									</Text>
								)}
							</Box>

							{/* Footer Buttons */}
							<Reveal if={hasPendingChanges}>
								<Divider />
								<HStack p={rem(16)} justifyContent="flex-end" spacing={rem(8)}>
									<Button variant="alpha" colorScheme="secondary" size="md" onClick={handleCancel}>
										{t('bonus_feed.additional_filters.cancel')}
									</Button>
									<Spacer />
									<Button variant="solid" colorScheme="primary" size="md" onClick={handleApply}>
										{t('bonus_feed.additional_filters.apply')}
									</Button>
								</HStack>
							</Reveal>
						</VStack>
					</PopoverBody>
				</PopoverContent>
			</Portal>
		</Popover>
	);
};

interface FilterCheckboxProps {
	option: Option;
	isChecked: boolean;
	onChange: () => void;
	icon?: ReactNode;
	section: 'types' | 'hashtags';
}

const FilterCheckbox = ({ option, isChecked, onChange, icon, section }: FilterCheckboxProps) => {
	const { t } = useTranslation('recognition');

	return (
		<HStack as="label" justifyContent="space-between" cursor="pointer" p={rem(8)} _hover={{ bg: 'secondary.bgAlpha' }} borderRadius="sm">
			<HStack spacing={rem(4)}>
				{icon}
				<Text fontSize="md">{section === 'types' ? t(`bonus_feed.additional_filters.types.${option.value}`, option.name) : option.name}</Text>
			</HStack>
			<Checkbox
				isChecked={isChecked}
				onChange={onChange}
				onKeyDown={(e) => {
					if (e.key === 'Enter') {
						e.preventDefault();
						onChange();
					}
				}}
				size="md"
			/>
		</HStack>
	);
};
